/**
 * Os achados com destino e grau e a conferência da análise (E152.F3, ADR-0103 §5.6–§5.7).
 *
 * A análise de riscos não executada é dita como tal, nunca como "sem achados". Cada achado mostra
 * para onde vai (laudo, orientações reservadas ou ambos) e, quando é risco, o grau com o fundamento
 * atingido, o argumento contrário, a resposta (ou a falta dela) e a providência. A conferência
 * lista os critérios como o servidor os avaliou.
 */
import { Badge } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { AchadoData, ResultadoDaFormalizacaoData } from '@/lib/formalizacaoAvaliacao';

const t = ptBR.formalizacaoAvaliacao;

const _GRAU = { alto: 'danger', medio: 'warning', baixo: 'neutral' } as const;
const _CRITERIO = {
  passou: 'success',
  falhou: 'danger',
  inconclusivo: 'warning',
  nao_se_aplica_justificado: 'neutral',
} as const;

function Achado({ achado }: { achado: AchadoData }): JSX.Element {
  const { risco } = achado;
  return (
    <li
      className="space-y-sc-1 border-l-2 border-border pl-sc-3"
      data-testid="formalizacao-achado"
      data-natureza={achado.natureza}
    >
      <p className="text-fg">
        {t.naturezaDoAchado[achado.natureza]}{' '}
        {achado.destinos.map((d) => (
          <Badge key={d} variant="outline">
            {t.destino[d]}
          </Badge>
        ))}{' '}
        {risco ? <Badge variant={_GRAU[risco.grau]}>{t.grau[risco.grau]}</Badge> : null}
      </p>
      <p className="text-fg">{achado.fato}</p>
      <p className="text-fg-muted">{achado.motivo_do_destino}</p>
      {risco ? (
        <dl className="grid gap-x-sc-3 gap-y-sc-1 text-fg-muted sm:grid-cols-[12rem_minmax(0,1fr)]">
          <dt>{t.risco.fundamento}</dt>
          <dd className="text-fg">{risco.fundamento_atingido}</dd>
          <dt>{t.risco.argumento}</dt>
          <dd className="text-fg">{risco.argumento_contrario}</dd>
          <dt>{t.risco.resposta}</dt>
          <dd className="text-fg">{risco.resposta ?? t.risco.semResposta}</dd>
          <dt>{t.risco.providencia}</dt>
          <dd className="text-fg">{risco.providencia}</dd>
        </dl>
      ) : null}
    </li>
  );
}

export function AchadosDaFormalizacao({
  resultado,
}: {
  resultado: ResultadoDaFormalizacaoData;
}): JSX.Element {
  const { achados, veracidade } = resultado;
  return (
    <>
      <section className="space-y-sc-2 text-body-sm" data-testid="formalizacao-achados">
        <h5 className="font-semibold text-fg">{t.achados}</h5>
        <p className="text-fg-muted" data-estado={achados.estado}>
          {achados.estado === 'nao_executada'
            ? t.naoExecutada
            : achados.achados.length === 0
              ? t.semAchados
              : achados.cobertura}
        </p>
        {achados.achados.length > 0 ? (
          <ul className="space-y-sc-2">
            {achados.achados.map((a) => (
              <Achado key={a.id} achado={a} />
            ))}
          </ul>
        ) : null}
        <p className="text-caption text-fg-dim">
          {t.criterioDoGrau.replace('{criterio}', achados.criterio)}
        </p>
      </section>
      <section className="space-y-sc-2 text-body-sm" data-testid="formalizacao-veracidade">
        <h5 className="font-semibold text-fg">{t.veracidade}</h5>
        <ul className="space-y-sc-1">
          {veracidade.criterios.map((c) => (
            <li key={c.chave} data-criterio={c.chave} data-estado={c.estado}>
              <Badge variant={_CRITERIO[c.estado]}>{t.estadoDoCriterio[c.estado]}</Badge>{' '}
              <span className="text-fg">{t.criterio[c.chave]}</span>
              <span className="block text-fg-muted">{c.detalhe}</span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
